import { Container, Graphics } from 'pixi.js';
import { deriveSeed, makeRng, rand, randRange, TAU, type PlanetBody, type PlanetPalette, type Temperament } from '@playnets/sim';

/**
 * PROCEDURAL PLANET BODIES
 * ========================
 *
 * No planet in this game has a sprite. Each one is built from its `PlanetBody`
 * recipe and a seed, which means a new planet is a block of JSON and its
 * evolutions can genuinely change its art (§5.1) rather than tinting it.
 *
 * Every body is the same four-layer stack, and the personality lives in how
 * the layers are parameterised:
 *
 *   halo     — atmosphere, soft, sits outside the silhouette
 *   shell    — the crust proper, an irregular lobed polygon
 *   feature  — the ring of things around the core. This is the strongest read:
 *              shards / crystals / cells / plates / vents / lattice
 *   core     — a flat disc with a heavy rim, the planet's eye
 *
 * The riso look comes from deliberate misregistration: the shell is drawn
 * twice, a few units apart, in two passes of flat colour. Nothing is shaded.
 */

export interface PlanetBodyOptions {
  body: PlanetBody;
  palette: PlanetPalette;
  /** Draw radius in whatever units the parent container uses. */
  radius: number;
  /** Seed for the silhouette. Same seed → same planet, every session. */
  seed: number;
  /** Skip the halo when drawing into a tight portrait frame. */
  halo?: boolean;
}

interface Pt {
  x: number;
  y: number;
}

function lobedRing(
  seed: number,
  radius: number,
  lobes: number,
  wobble: number,
  steps = 96,
): Pt[] {
  const rng = makeRng(seed);
  const phase = rand(rng) * TAU;
  const lobePhase = rand(rng) * TAU;
  // Two harmonics: a slow lobe count plus a faster ripple, so the silhouette
  // reads as "irregular" rather than "wavy".
  const ripple = randRange(rng, 1.8, 3.4);
  const pts: Pt[] = [];
  for (let i = 0; i < steps; i++) {
    const a = (i / steps) * TAU;
    const w =
      Math.sin(a * lobes + lobePhase) * wobble +
      Math.sin(a * lobes * ripple + phase) * wobble * 0.38;
    const r = radius * (1 + w);
    pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
  }
  return pts;
}

function flatPoly(pts: Pt[]): number[] {
  const out: number[] = [];
  for (const p of pts) out.push(p.x, p.y);
  return out;
}

/** Build the whole body. Returns a container ready to be rotated and scaled. */
export function buildPlanetBody(opts: PlanetBodyOptions): Container {
  const { body, palette, radius, seed } = opts;
  const root = new Container();
  const rng = makeRng(deriveSeed(seed, 0x51ce));

  // The halo follows the shell's own silhouette. A plain circle behind a lobed
  // crust prints as a hard grey donut, which flattens every planet into the
  // same shape at a glance — exactly what the recipe exists to avoid.
  const shell = lobedRing(
    deriveSeed(seed, 0x5e11),
    radius,
    body.shellLobes,
    body.shellWobble,
  );

  if (opts.halo !== false && body.halo > 0) {
    root.addChild(drawHalo(body, palette, shell));
  }

  root.addChild(drawShell(body, palette, radius, shell));

  if (body.bands > 0) {
    root.addChild(drawBands(body, palette, radius, deriveSeed(seed, 0xba2d)));
  }
  if (body.speckle > 0) {
    root.addChild(drawSpeckle(body, palette, radius, deriveSeed(seed, 0x59ec)));
  }

  root.addChild(drawFeature(body, palette, radius, deriveSeed(seed, 0xfea7), rng));
  root.addChild(drawCore(body, palette, radius));

  return root;
}

function scalePts(pts: Pt[], k: number): Pt[] {
  return pts.map((p) => ({ x: p.x * k, y: p.y * k }));
}

function drawHalo(body: PlanetBody, palette: PlanetPalette, shell: Pt[]): Graphics {
  const g = new Graphics();
  // Two flat passes, not a gradient — this is a print, not a render. Opaque
  // enough to read as atmosphere: a low-alpha pale fill over a near-black void
  // resolves to grey, which looks like a grubby outline rather than air.
  g.poly(flatPoly(scalePts(shell, 1 + body.halo))).fill({
    color: palette.halo,
    alpha: 0.3,
  });
  g.poly(flatPoly(scalePts(shell, 1 + body.halo * 0.5))).fill({
    color: palette.halo,
    alpha: 0.45,
  });
  return g;
}

function drawShell(
  body: PlanetBody,
  palette: PlanetPalette,
  R: number,
  pts: Pt[],
): Graphics {
  const g = new Graphics();
  const poly = flatPoly(pts);

  // Misregistration: the dark plate prints a few units off-centre.
  const off = Math.max(2, R * 0.03);
  g.poly(poly.map((v, i) => (i % 2 === 0 ? v + off : v + off * 0.6))).fill({
    color: palette.crustDark,
  });
  g.poly(poly).fill({ color: palette.crust });

  // A light crescent, flat-filled, as the second colour pass.
  const crescent = pts.filter((_, i) => i < pts.length * 0.42);
  if (crescent.length > 2) {
    const inner = crescent
      .slice()
      .reverse()
      .map((p) => ({ x: p.x * 0.72, y: p.y * 0.72 }));
    g.poly(flatPoly([...crescent, ...inner])).fill({
      color: palette.crustLight,
      alpha: 0.5,
    });
  }
  return g;
}

/** Gas-giant banding. Ellipses clipped to the shell by simple over-draw. */
function drawBands(
  body: PlanetBody,
  palette: PlanetPalette,
  R: number,
  seed: number,
): Container {
  const c = new Container();
  const g = new Graphics();
  const rng = makeRng(seed);
  for (let i = 0; i < body.bands; i++) {
    const y = randRange(rng, -R * 0.8, R * 0.8);
    const h = randRange(rng, R * 0.06, R * 0.16);
    const w = Math.sqrt(Math.max(R * R - y * y, 1)) * randRange(rng, 0.8, 1.0);
    g.ellipse(randRange(rng, -R * 0.1, R * 0.1), y, w, h).fill({
      color: i % 2 === 0 ? palette.crustLight : palette.crustDark,
      alpha: 0.35,
    });
  }
  const mask = new Graphics().circle(0, 0, R * 0.99).fill({ color: 0xffffff });
  c.addChild(g, mask);
  g.mask = mask;
  return c;
}

/** Halftone-ish speckle. Ice and vegetation both read through this. */
function drawSpeckle(
  body: PlanetBody,
  palette: PlanetPalette,
  R: number,
  seed: number,
): Container {
  const c = new Container();
  const g = new Graphics();
  const rng = makeRng(seed);
  const count = Math.round(body.speckle * 220);
  for (let i = 0; i < count; i++) {
    // Uniform over the disc, biased outward so the core stays clean.
    const a = rand(rng) * TAU;
    const rr = R * (0.45 + Math.sqrt(rand(rng)) * 0.55);
    const size = randRange(rng, R * 0.012, R * 0.042);
    g.ellipse(Math.cos(a) * rr, Math.sin(a) * rr, size, size * randRange(rng, 0.6, 1.1)).fill({
      color: palette.crustLight,
      alpha: randRange(rng, 0.45, 0.9),
    });
  }
  const mask = new Graphics().circle(0, 0, R * 0.98).fill({ color: 0xffffff });
  c.addChild(g, mask);
  g.mask = mask;
  return c;
}

function drawCore(body: PlanetBody, palette: PlanetPalette, R: number): Graphics {
  const g = new Graphics();
  const cr = R * body.coreRatio;
  g.circle(0, 0, cr * 1.1).fill({ color: palette.coreRim, alpha: 0.85 });
  g.circle(0, 0, cr).fill({ color: palette.core });
  g.circle(0, 0, cr)
    .stroke({ width: Math.max(2, R * 0.022), color: palette.coreRim, alignment: 0.5 });
  return g;
}

/* ------------------------------------------------------------------ *
 * Feature rings — the silhouette read
 * ------------------------------------------------------------------ */

function drawFeature(
  body: PlanetBody,
  palette: PlanetPalette,
  R: number,
  seed: number,
  _rng: ReturnType<typeof makeRng>,
): Graphics {
  const g = new Graphics();
  const rng = makeRng(seed);
  const inner = R * body.coreRatio;
  const outer = R * 0.99;

  switch (body.feature) {
    case 'shards':
      drawShards(g, palette, body, rng, inner, outer);
      break;
    case 'crystals':
      drawCrystals(g, palette, body, rng, inner, outer);
      break;
    case 'cells':
      drawCells(g, palette, body, rng, inner, outer);
      break;
    case 'plates':
      drawPlates(g, palette, body, rng, inner, outer);
      break;
    case 'vents':
      drawVents(g, palette, body, rng, inner, outer, R);
      break;
    case 'lattice':
      drawLattice(g, palette, body, rng, inner, outer);
      break;
  }
  return g;
}

/** Long ice spines, apexes pointing inward at the core. */
function drawShards(
  g: Graphics,
  p: PlanetPalette,
  body: PlanetBody,
  rng: ReturnType<typeof makeRng>,
  inner: number,
  outer: number,
): void {
  const n = body.featureCount;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU + randRange(rng, -0.12, 0.12) * body.featureChaos;
    const reach = body.featureReach * randRange(rng, 0.65, 1.25);
    const tip = inner + (outer - inner) * (1 - reach) * 0.4;
    const base = outer * randRange(rng, 0.86, 1.0);
    const halfW = randRange(rng, 0.035, 0.12) * (1 + body.featureChaos);

    const tx = Math.cos(a) * tip;
    const ty = Math.sin(a) * tip;
    const b1 = a - halfW;
    const b2 = a + halfW;

    g.poly([
      tx,
      ty,
      Math.cos(b1) * base,
      Math.sin(b1) * base,
      Math.cos(b2) * base,
      Math.sin(b2) * base,
    ]).fill({ color: p.feature });

    // A single flat highlight facet, offset — the second print pass.
    g.poly([
      tx,
      ty,
      Math.cos(b1) * base,
      Math.sin(b1) * base,
      Math.cos(a - halfW * 0.15) * base * 0.94,
      Math.sin(a - halfW * 0.15) * base * 0.94,
    ]).fill({ color: p.featureLight, alpha: 0.55 });
  }
}

/** Clustered crystal spines: a long blade with small facets crowding its base. */
function drawCrystals(
  g: Graphics,
  p: PlanetPalette,
  body: PlanetBody,
  rng: ReturnType<typeof makeRng>,
  inner: number,
  outer: number,
): void {
  const n = body.featureCount;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU + randRange(rng, -0.2, 0.2) * body.featureChaos;
    const len = randRange(rng, 0.5, 1.0) * body.featureReach;
    const start = inner * randRange(rng, 0.98, 1.06);
    const end = start + (outer - start) * len;
    // Blades are chunky. Thin spines print as arrows, not crystal.
    const w = randRange(rng, 0.075, 0.17);

    g.poly([
      Math.cos(a) * end,
      Math.sin(a) * end,
      Math.cos(a - w) * start,
      Math.sin(a - w) * start,
      Math.cos(a + w) * start,
      Math.sin(a + w) * start,
    ]).fill({ color: p.feature });

    // One lit face down the blade, so it reads as a solid with an edge.
    g.poly([
      Math.cos(a) * end,
      Math.sin(a) * end,
      Math.cos(a - w) * start,
      Math.sin(a - w) * start,
      Math.cos(a - w * 0.1) * start,
      Math.sin(a - w * 0.1) * start,
    ]).fill({ color: p.featureLight, alpha: 0.6 });

    // Facet clusters crowding the base, alternating light and dark.
    const facets = 2 + Math.floor(rand(rng) * 3);
    for (let f = 0; f < facets; f++) {
      const fa = a + randRange(rng, -w * 1.6, w * 1.6);
      const fr = start * randRange(rng, 1.0, 1.25);
      const fw = randRange(rng, 0.05, 0.1);
      const fl = fr * randRange(rng, 0.16, 0.34);
      g.poly([
        Math.cos(fa) * (fr + fl),
        Math.sin(fa) * (fr + fl),
        Math.cos(fa - fw) * fr,
        Math.sin(fa - fw) * fr,
        Math.cos(fa + fw) * fr,
        Math.sin(fa + fw) * fr,
      ]).fill({ color: f % 2 === 0 ? p.featureLight : p.featureDark, alpha: 0.92 });
    }
  }
}

/**
 * Magma cells: irregular crust plates floating on hot rock, with the glow
 * showing through the cracks between them. Every cell has to differ in both
 * dimensions — a fixed angular step and a fixed radial band prints a roulette
 * wheel, which is the one thing a lava world must not look like.
 */
function drawCells(
  g: Graphics,
  p: PlanetPalette,
  body: PlanetBody,
  rng: ReturnType<typeof makeRng>,
  inner: number,
  outer: number,
): void {
  const band = outer - inner;

  // The hot substrate first — every crack between cells glows.
  g.circle(0, 0, outer).fill({ color: p.featureLight });

  // Walk the ring laying cells end to end, each with its own width, so the
  // seams never line up into spokes.
  let a = rand(rng) * TAU;
  const target = TAU;
  let swept = 0;
  const meanWidth = TAU / body.featureCount;

  while (swept < target) {
    const width = meanWidth * randRange(rng, 0.55, 1.9);
    const gap = meanWidth * randRange(rng, 0.1, 0.3) * (0.4 + body.featureChaos);

    // Each cell spans a random slice of the band rather than a fixed ring.
    const r0 = inner + band * randRange(rng, -0.02, 0.34);
    const r1 = r0 + band * randRange(rng, 0.34, 0.78);
    const skew = randRange(rng, -0.06, 0.06) * body.featureChaos;
    const a0 = a;
    const a1 = a + width;

    g.poly([
      Math.cos(a0) * r0,
      Math.sin(a0) * r0,
      Math.cos(a1) * r0,
      Math.sin(a1) * r0,
      Math.cos(a1 + skew) * r1,
      Math.sin(a1 + skew) * r1,
      Math.cos(a0 + skew) * r1,
      Math.sin(a0 + skew) * r1,
    ]).fill({ color: rand(rng) < 0.35 ? p.featureDark : p.feature });

    // The dark pit each plate carries.
    if (rand(rng) < 0.5) {
      const mr = (r0 + r1) * 0.5;
      const ma = (a0 + a1) * 0.5;
      const pit = Math.min((r1 - r0) * 0.2, width * mr * 0.28);
      g.circle(Math.cos(ma) * mr, Math.sin(ma) * mr, pit).fill({
        color: p.featureDark,
        alpha: 0.9,
      });
    }

    a += width + gap;
    swept += width + gap;
  }
}

/** Continental plates: broad tiles with visible seams. */
function drawPlates(
  g: Graphics,
  p: PlanetPalette,
  body: PlanetBody,
  rng: ReturnType<typeof makeRng>,
  inner: number,
  outer: number,
): void {
  const n = body.featureCount;
  const band = (outer - inner) * body.featureReach;
  for (let i = 0; i < n; i++) {
    if (rand(rng) < 0.22) continue; // gaps are what make it read as land
    const a = (i / n) * TAU;
    const half = (TAU / n) * randRange(rng, 0.3, 0.48);
    const r0 = inner + band * randRange(rng, 0.0, 0.35);
    const r1 = r0 + band * randRange(rng, 0.45, 1.0);
    const wob = randRange(rng, -0.05, 0.05) * body.featureChaos;

    // Mostly the base tone, some shadowed, a few catching the light — a
    // continent shelf, not a mosaic of equally bright tiles.
    const roll = rand(rng);
    const colour = roll < 0.2 ? p.featureLight : roll < 0.45 ? p.featureDark : p.feature;

    g.poly([
      Math.cos(a - half) * r0,
      Math.sin(a - half) * r0,
      Math.cos(a + half) * r0,
      Math.sin(a + half) * r0,
      Math.cos(a + half + wob) * r1,
      Math.sin(a + half + wob) * r1,
      Math.cos(a - half + wob) * r1,
      Math.sin(a - half + wob) * r1,
    ]).fill({ color: colour, alpha: 0.95 });
  }
}

/** Rifts: dark wedges cutting through the crust with a lit edge. */
function drawVents(
  g: Graphics,
  p: PlanetPalette,
  body: PlanetBody,
  rng: ReturnType<typeof makeRng>,
  inner: number,
  outer: number,
  R: number,
): void {
  const n = body.featureCount;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU + randRange(rng, -0.25, 0.25) * body.featureChaos;
    const w0 = randRange(rng, 0.01, 0.03);
    const w1 = randRange(rng, 0.05, 0.13) * (1 + body.featureChaos);
    const end = inner + (outer - inner) * body.featureReach * randRange(rng, 0.7, 1.15);

    g.poly([
      Math.cos(a - w0) * inner,
      Math.sin(a - w0) * inner,
      Math.cos(a + w0) * inner,
      Math.sin(a + w0) * inner,
      Math.cos(a + w1) * end,
      Math.sin(a + w1) * end,
      Math.cos(a - w1) * end,
      Math.sin(a - w1) * end,
    ]).fill({ color: p.feature });

    // One lit lip, wide enough to read as depth rather than a hairline.
    g.poly([
      Math.cos(a + w0) * inner,
      Math.sin(a + w0) * inner,
      Math.cos(a + w1) * end,
      Math.sin(a + w1) * end,
      Math.cos(a + w1 * 0.42) * end,
      Math.sin(a + w1 * 0.42) * end,
    ]).fill({ color: p.featureLight, alpha: 0.8 });
    void R;
  }
}

/** Engineered: radial struts and concentric arcs. Nothing organic about it. */
function drawLattice(
  g: Graphics,
  p: PlanetPalette,
  body: PlanetBody,
  rng: ReturnType<typeof makeRng>,
  inner: number,
  outer: number,
): void {
  const n = body.featureCount;
  const band = (outer - inner) * body.featureReach;
  const width = Math.max(1.5, band * 0.09);

  for (let ring = 1; ring <= 3; ring++) {
    const rr = inner + (band * ring) / 3;
    g.circle(0, 0, rr).stroke({
      width: width * (ring === 2 ? 1.6 : 1),
      color: ring === 2 ? p.featureLight : p.feature,
      alpha: 0.9,
    });
  }

  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU + randRange(rng, -0.02, 0.02) * body.featureChaos;
    const r1 = inner + band * (rand(rng) < 0.3 ? 1.12 : 1.0);
    g.moveTo(Math.cos(a) * inner, Math.sin(a) * inner)
      .lineTo(Math.cos(a) * r1, Math.sin(a) * r1)
      .stroke({ width, color: i % 4 === 0 ? p.featureLight : p.feature });
  }
}

/* ------------------------------------------------------------------ *
 * The face
 *
 * It sits on the core and does NOT rotate with the crust — a face that turns
 * upside down stops being a face. Three behaviours carry all the character:
 * it blinks, it watches whatever is closest to killing it, and its mouth
 * changes as Core Integrity drops.
 * ------------------------------------------------------------------ */

interface FaceParts {
  root: Container;
  whites: Graphics;
  pupils: Graphics;
  lids: Graphics;
  mouth: Graphics;
  /** Half the distance between the eyes, in draw units. */
  spacing: number;
  eyeR: number;
}

function buildFace(body: PlanetBody, palette: PlanetPalette, R: number): FaceParts {
  const root = new Container();
  const core = R * body.coreRatio;
  const style = body.face;

  // Hollow worlds have no whites — just two lit points in the dark.
  const hasWhites = style !== 'hollow';
  const eyeR = core * (style === 'machine' ? 0.24 : style === 'ancient' ? 0.34 : 0.31);
  const spacing = core * (style === 'ancient' ? 0.46 : 0.4);
  const eyeY = -core * (style === 'sleepy' ? 0.05 : 0.12);

  const white = pickReadable(palette.core, palette.featureLight, palette.crustLight);
  const dark = pickContrast(white);

  const whites = new Graphics();
  if (hasWhites) {
    for (const sx of [-1, 1]) {
      if (style === 'machine') {
        whites
          .roundRect(sx * spacing - eyeR, eyeY - eyeR, eyeR * 2, eyeR * 2, eyeR * 0.35)
          .fill({ color: white });
      } else {
        whites.ellipse(sx * spacing, eyeY, eyeR, eyeR * 1.12).fill({ color: white });
      }
    }
  }
  root.addChild(whites);

  const pupils = new Graphics();
  root.addChild(pupils);

  // Lids do the blinking and the heavy-lidded look, drawn in the core colour so
  // they read as the eye closing rather than as a bar across the face.
  const lids = new Graphics();
  root.addChild(lids);

  const mouth = new Graphics();
  root.addChild(mouth);

  return { root, whites, pupils, lids, mouth, spacing, eyeR };
}

/** Pick whichever candidate has the most contrast against the core. */
function pickReadable(against: number, ...options: number[]): number {
  let best = 0xffffff;
  let bestScore = -1;
  for (const o of [...options, 0xfff6e5, 0x1b1a2e]) {
    const score = Math.abs(luma(o) - luma(against));
    if (score > bestScore) {
      bestScore = score;
      best = o;
    }
  }
  return best;
}

function pickContrast(against: number): number {
  return luma(against) > 0.5 ? 0x1b1a2e : 0xfff6e5;
}

function luma(c: number): number {
  const r = ((c >> 16) & 0xff) / 255;
  const g = ((c >> 8) & 0xff) / 255;
  const b = (c & 0xff) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/* ------------------------------------------------------------------ *
 * Temperament-driven idle motion
 * ------------------------------------------------------------------ */

/**
 * Two planets with identical stats should not feel the same to sit in front
 * of for ten minutes. Temperament is what makes them differ: a restless world
 * breathes visibly and twitches; a glacial one barely moves at all.
 */
export class PlanetBodyView {
  /** Everything. Scale/alpha effects go here. */
  readonly view = new Container();
  /** The crust, which rotates. */
  private readonly bodyView: Container;
  /** The face, which does not — a face that turns upside down isn't a face. */
  private readonly face: FaceParts;

  private t = 0;
  /** 0..1, rises when the core takes a hit and decays back down. */
  private stress = 0;
  private blinkIn: number;
  private blink = 0;
  /** Smoothed look direction, so the eyes glide instead of snapping. */
  private lookX = 0;
  private lookY = 0;
  private health = 1;

  /** Lids are drawn in the core colour so a blink reads as the eye closing. */
  private readonly coreColour: number;

  constructor(
    private readonly body: PlanetBody,
    palette: PlanetPalette,
    private readonly temperament: Temperament,
    private readonly radius: number,
    seed: number,
  ) {
    this.coreColour = palette.core;
    this.bodyView = buildPlanetBody({ body, palette, radius, seed });
    this.face = buildFace(body, palette, radius);
    this.view.addChild(this.bodyView, this.face.root);
    // Restless worlds blink more often.
    this.blinkIn = 2 + (1 - temperament.restlessness) * 4;
    this.drawFace();
  }

  /** Call on a core hit. Reactivity decides how much the planet shows it. */
  flinch(strength = 1): void {
    this.stress = Math.min(1, this.stress + strength * this.temperament.reactivity);
    // A hit makes it blink, which reads as a wince.
    this.blink = 0.18;
  }

  /**
   * @param lookAt  Bearing of whatever is closest to killing it, or null when
   *                the sky is clear. The eyes follow it.
   * @param health  Core Integrity as a fraction. Drives the mouth.
   */
  update(dt: number, rotation: number, lookAt: number | null = null, health = 1): void {
    this.t += dt;
    this.stress = Math.max(0, this.stress - dt * 1.6);
    this.health = health;

    const tem = this.temperament;

    // Resting heart rate. Restless worlds breathe visibly; Glacia does not.
    const breath = Math.sin((this.t / tem.pulsePeriod) * TAU) * 0.008 * tem.restlessness;
    // Jitter quantises rotation into visible steps instead of a smooth drift.
    const steps = 1 + Math.round((1 - tem.jitter) * 200);
    const stepped = tem.jitter > 0.2 ? Math.round(rotation * steps) / steps : rotation;

    const flinch = this.stress * this.stress;
    this.bodyView.rotation = stepped;
    this.view.scale.set(1 + breath + flinch * 0.05);
    this.view.alpha = 1 - flinch * 0.12;

    // Blink timer.
    if (this.blink > 0) {
      this.blink -= dt;
    } else {
      this.blinkIn -= dt;
      if (this.blinkIn <= 0) {
        this.blink = 0.13;
        this.blinkIn = 2.5 + (1 - tem.restlessness) * 5;
      }
    }

    // Eyes glide toward the threat rather than snapping to it.
    const tx = lookAt === null ? 0 : Math.cos(lookAt);
    const ty = lookAt === null ? 0 : Math.sin(lookAt);
    const ease = Math.min(1, dt * 6);
    this.lookX += (tx - this.lookX) * ease;
    this.lookY += (ty - this.lookY) * ease;

    this.drawFace();
  }

  private drawFace(): void {
    const { pupils, lids, mouth, spacing, eyeR } = this.face;
    const style = this.body.face;
    const core = this.radius * this.body.coreRatio;
    const eyeY = -core * (style === 'sleepy' ? 0.05 : 0.12);

    const dark = style === 'hollow' ? 0xff6b35 : 0x1b1a2e;
    const pupilR = eyeR * (style === 'machine' ? 0.55 : style === 'hollow' ? 0.7 : 0.5);
    // Generous travel. At playfield scale the eyes are ~14 units across, so a
    // timid pupil shift is invisible — and the look-at is doing real work,
    // pointing at whatever is closest to the crust.
    const reach = eyeR * 0.8;

    pupils.clear();
    for (const sx of [-1, 1]) {
      const px = sx * spacing + this.lookX * reach;
      const py = eyeY + this.lookY * reach;
      if (style === 'machine') {
        pupils.rect(px - pupilR, py - pupilR * 1.3, pupilR * 2, pupilR * 2.6).fill({ color: dark });
      } else {
        pupils.circle(px, py, pupilR).fill({ color: dark });
        // A catchlight. Two pixels of highlight is most of "alive".
        pupils
          .circle(px - pupilR * 0.3, py - pupilR * 0.35, pupilR * 0.32)
          .fill({ color: 0xffffff, alpha: 0.75 });
      }
    }

    // Lids: blinking, plus the permanently heavy-lidded looks.
    const rest = style === 'sleepy' ? 0.55 : style === 'ancient' ? 0.4 : 0;
    const closed = Math.max(rest, this.blink > 0 ? 1 : 0);
    lids.clear();
    if (closed > 0 && style !== 'hollow') {
      const h = eyeR * 2.4 * closed;
      for (const sx of [-1, 1]) {
        lids
          .rect(sx * spacing - eyeR * 1.3, eyeY - eyeR * 1.3, eyeR * 2.6, h)
          .fill({ color: this.coreColour });
      }
    }

    // The mouth carries the health read. No numbers involved.
    const hurt = 1 - this.health;
    const mw = core * 0.42;
    const my = core * 0.44;
    const curve = style === 'fierce' ? -0.5 : style === 'calm' ? 0.55 : 0.15;
    // Smile flips to a grimace as Integrity drops.
    const bend = (curve - hurt * 1.3) * core * 0.3;
    mouth.clear();
    if (style === 'machine' || style === 'hollow') {
      // No mouth. A line for the machine; nothing at all for the hollow one.
      if (style === 'machine') {
        mouth.rect(-mw * 0.6, my, mw * 1.2, core * 0.06).fill({ color: dark, alpha: 0.8 });
      }
    } else {
      mouth.moveTo(-mw, my);
      mouth.quadraticCurveTo(0, my + bend, mw, my);
      mouth.stroke({ width: Math.max(2, core * 0.09), color: dark, cap: 'round' });
    }
  }

}
